/**
 * Reglas puras de la pantalla del asistente de migración (v2/migracion, application/views/noble/migracion/index.php).
 * Spec docs/superpowers/specs/2026-10-20-asistente-migracion-design.md §4.1 y §5.8; plan
 * docs/superpowers/plans/2026-10-20-asistente-migracion-e1.md, Task 11.
 *
 * Sin DOM ni red: la vista las lee por window.FDMigracion.reglas (su script en línea no pasa por Vite) y la píldora
 * de la barra (Task 12) usa intervaloSondeo. Lo que llega del servidor puede venir incompleto: ninguna lanza.
 */

var MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Los renglones que son CFDIs (los demás son catálogos). */
var CFDIS = { emitidos: 1, recibidos: 1 };

function entero(v) {
  if (v === null || v === undefined || v === '') return null;   // Number(null) sería 0: «no hay» no es cero
  var n = Number(v);
  return isFinite(n) ? Math.floor(n) : null;
}

/** 'AAAA-MM-DD…' → {a, m, d} o null si no es una fecha legible. */
function partesFecha(iso) {
  var m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso === null || iso === undefined ? '' : iso));
  if (!m) return null;
  var a = +m[1], mes = +m[2], d = +m[3];
  if (mes < 1 || mes > 12 || d < 1 || d > 31) return null;
  return { a: a, m: mes, d: d };
}

/** El porcentaje que se pinta: nunca baja del que ya se mostró; entero de 0 a 100. */
export function porcentajeVisible(nuevo, anterior) {
  var a = entero(anterior);
  var n = entero(nuevo);
  var r = Math.max(n === null ? 0 : n, a === null ? 0 : a);
  return r < 0 ? 0 : (r > 100 ? 100 : r);
}

/** «Desde nov 2025»: lo que cubre un chip de alcance (su `desde`, AAAA-MM-DD). Vacío si no hay fecha. */
export function etiquetaDesde(iso) {
  var f = partesFecha(iso);
  return f ? 'Desde ' + MESES[f.m - 1] + ' ' + f.a : '';
}

/** «14 mar 2029»: la vigencia de la e.firma. Vacío si no hay fecha. */
export function fechaCorta(iso) {
  var f = partesFecha(iso);
  return f ? f.d + ' ' + MESES[f.m - 1] + ' ' + f.a : '';
}

/**
 * El paso del asistente que toca según el estado del servidor: 1 sin credencial (en inicio), 2 en inicio con
 * credencial, 3 migrando (aunque la credencial se haya caído: el aviso de espera lo dice), 4 con la lista de productos
 * por elegir y 5 listo.
 */
export function pasoDeFase(fase, credencial) {
  if (fase === 'migrando') return 3;
  if (fase === 'productos') return 4;
  if (fase === 'listo') return 5;
  return (credencial && credencial.tipo) ? 2 : 1;
}

/** Cuántos de `ids` están en `seleccion` (Set o arreglo; los ids se comparan como texto). */
export function conteoSeleccion(ids, seleccion) {
  var lista = Array.isArray(ids) ? ids : [];
  var marcados = {};
  var fuente = seleccion instanceof Set ? Array.from(seleccion) : (Array.isArray(seleccion) ? seleccion : []);
  for (var i = 0; i < fuente.length; i++) marcados[String(fuente[i])] = true;
  var n = 0;
  for (var j = 0; j < lista.length; j++) if (marcados[String(lista[j])]) n++;
  return { n: n, total: lista.length };
}

/** Una cifra entera con separador de miles (es-MX): 3108 → «3,108». */
export function numero(v) {
  var n = entero(v);
  if (n === null) n = 0;
  var s = String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (n < 0 ? '-' : '') + s;
}

/** «$25,000.00»: el precio unitario de una sugerencia, sin impuestos. */
export function formatoPrecio(v) {
  var n = Number(v);
  if (!isFinite(n)) n = 0;
  var centavos = Math.round(Math.abs(n) * 100 + 1e-6);
  var ent = Math.floor(centavos / 100);
  var dec = String(centavos % 100);
  return (n < 0 ? '-' : '') + '$' + numero(ent) + '.' + (dec.length < 2 ? '0' + dec : dec);
}

/** «Importar 96 productos» / «Importar 1 producto». */
export function textoBotonImportar(n) {
  var k = entero(n);
  if (k === null || k < 0) k = 0;
  return 'Importar ' + numero(k) + (k === 1 ? ' producto' : ' productos');
}

/**
 * Los eventos de `next` que no venían en `prev` (el sondeo anterior). El servidor puede repetir un evento entre dos
 * sondeos mientras no guarda su foto nueva: así el orbe no dispara dos veces la misma ráfaga ni el mismo destello.
 * Cada evento es [tipo, clave] ('bloque' o 'renglon_listo').
 */
export function eventosNuevos(prev, next) {
  var vistos = {};
  var antes = Array.isArray(prev) ? prev : [];
  for (var i = 0; i < antes.length; i++) {
    if (Array.isArray(antes[i])) vistos[antes[i][0] + '|' + antes[i][1]] = true;
  }
  var r = [];
  var despues = Array.isArray(next) ? next : [];
  for (var j = 0; j < despues.length; j++) {
    var e = despues[j];
    if (!Array.isArray(e)) continue;
    var k = e[0] + '|' + e[1];
    if (vistos[k]) continue;
    vistos[k] = true;
    r.push(e);
  }
  return r;
}

/**
 * Lo traído de un renglón de CFDIs: su campo numérico `traidos` (Migracion_asistente::renglon_cfdis lo manda desde la ola
 * final, C1). Sin él (un servidor anterior) o si no es un entero ≥ 0, se lee del texto como antes: la cifra con la que
 * empieza «3,108 traídos» o «1,240 de 4,512»; 0 en cualquier otro texto («Buscando en el SAT», «En espera», «2 meses sin
 * traer»). Los renglones de catálogos no cuentan.
 */
export function traidosDe(renglon) {
  if (!renglon || !CFDIS[renglon.clave]) return 0;
  var n = renglon.traidos;
  if (typeof n === 'number' && isFinite(n) && n >= 0) return Math.floor(n);
  var m = /^(\d[\d,]*)\s+(?:traíd|de\s)/.exec(String(renglon.texto === null || renglon.texto === undefined ? '' : renglon.texto));
  return m ? Number(m[1].replace(/,/g, '')) : 0;
}

/** {emitidos: n, recibidos: n}: lo traído de cada renglón de CFDIs que viene en la lista. */
export function traidosVistos(renglones) {
  var r = {};
  var lista = Array.isArray(renglones) ? renglones : [];
  for (var i = 0; i < lista.length; i++) {
    if (lista[i] && CFDIS[lista[i].clave]) r[lista[i].clave] = traidosDe(lista[i]);
  }
  return r;
}

/**
 * Los eventos que celebra el orbe en este sondeo y la marca nueva de lo traído: {eventos, traidos}.
 *  - 'renglon_listo', una sola vez (eventosNuevos contra el sondeo anterior).
 *  - 'bloque', cada vez que ese renglón trae más CFDIs que la última marca: el servidor repite ['bloque', clave]
 *    mientras no guarda su foto (durante una descarga larga, sondeo tras sondeo), así que comparar el evento contra el
 *    sondeo anterior dejaba callado al orbe después de la primera ráfaga.
 * La marca nunca baja.
 */
export function eventosOrbe(prev, next, renglones, traidos) {
  var actuales = traidosVistos(renglones);
  var marca = {};
  var k;
  var base = traidos || {};
  for (k in base) { if (Object.prototype.hasOwnProperty.call(base, k)) marca[k] = Number(base[k]) || 0; }
  var listos = {};
  eventosNuevos(prev, next).forEach(function (e) { if (e[0] === 'renglon_listo') listos[e[1]] = true; });
  var r = [];
  var ya = {};
  (Array.isArray(next) ? next : []).forEach(function (e) {
    if (!Array.isArray(e)) return;
    var clave = e[0] + '|' + e[1];
    if (ya[clave]) return;
    var celebra = e[0] === 'bloque' ? (actuales[e[1]] || 0) > (marca[e[1]] || 0) : (e[0] === 'renglon_listo' && !!listos[e[1]]);
    if (celebra) { ya[clave] = true; r.push(e); }
  });
  for (k in actuales) { if (Object.prototype.hasOwnProperty.call(actuales, k)) marca[k] = Math.max(marca[k] || 0, actuales[k]); }
  return { eventos: r, traidos: marca };
}

/**
 * Si el estado nuevo es de OTRA corrida (Premium que vuelve por años anteriores, o la primera corrida de la cuenta),
 * lo que la pantalla tenía de la anterior ya no vale: el porcentaje (que nunca baja) volvería a 0, la región en vivo,
 * los eventos y lo traído vistos, el aviso de «vista final», la lista de productos y la red encendida del orbe. Devuelve
 * ese reinicio, o null si es la misma corrida (la borrador que se convierte conserva su id) o el estado no trae una.
 */
export function reinicioPorCorrida(idAnterior, nuevo) {
  var id = (nuevo && nuevo.migracion_id !== null && nuevo.migracion_id !== undefined) ? String(nuevo.migracion_id) : null;
  var antes = (idAnterior === null || idAnterior === undefined) ? null : String(idAnterior);
  if (id === null || id === antes) return null;
  return {
    pct: 0, anunciado: null, eventosPrevios: [], traidos: traidosVistos(nuevo.renglones),
    vistaFinalAvisada: false, listaCargada: false, remontarOrbe: true,
  };
}

/**
 * Sin una interacción de la persona en este tiempo, los sondeos se detienen hasta la siguiente: cada AJAX renueva la
 * sesión, y FD.sesion promete que nada la renueva sin la persona. La píldora de la barra espera 10 minutos (R30); la
 * pantalla del asistente, 30 (R34: alguien puede quedarse mirando la esfera mientras migra).
 */
export var INACTIVIDAD_MS = 10 * 60 * 1000;
export var INACTIVIDAD_ASISTENTE_MS = 30 * 60 * 1000;

/**
 * ¿Sigue preguntando? Sí mientras la última interacción (pointerdown, keydown, foco de la ventana o volver a la pestaña)
 * fue hace menos de `limiteMs` (por omisión INACTIVIDAD_MS, el de la píldora; un límite que no es un número positivo
 * cuenta como ese). Un reloj que fue hacia atrás cuenta como reciente; sin fechas legibles, no.
 */
export function sondeoVigente(ultimaInteraccionMs, ahoraMs, limiteMs) {
  if (ultimaInteraccionMs === null || ultimaInteraccionMs === undefined || ahoraMs === null || ahoraMs === undefined) return false;
  var u = Number(ultimaInteraccionMs);
  var a = Number(ahoraMs);
  if (!isFinite(u) || !isFinite(a)) return false;
  var limite = (typeof limiteMs === 'number' && isFinite(limiteMs) && limiteMs > 0) ? limiteMs : INACTIVIDAD_MS;
  return a - u < limite;
}

/**
 * El sondeo de la pantalla solo trae la credencial en fase inicio, con espera de credencial o en el paso 1 (A4): si el
 * estado nuevo llega con credencial null, se queda la que la pantalla ya tenía. Devuelve un estado nuevo (no cambia el
 * que recibe); lo que no es un estado se devuelve tal cual.
 */
export function conservarCredencial(anterior, nuevo) {
  if (!nuevo || typeof nuevo !== 'object') return nuevo;
  if (nuevo.credencial !== null && nuevo.credencial !== undefined) return nuevo;
  var r = {};
  for (var k in nuevo) { if (Object.prototype.hasOwnProperty.call(nuevo, k)) r[k] = nuevo[k]; }
  r.credencial = (anterior && anterior.credencial) ? anterior.credencial : null;
  return r;
}

/** Cada cuánto se pregunta el estado: 4 s migrando con la pestaña a la vista, 15 s en otra fase, nada si está oculta. */
export function intervaloSondeo(o) {
  var op = o || {};
  if (!op.visible) return null;
  return op.fase === 'migrando' ? 4000 : 15000;
}

/**
 * El texto de un renglón partido en cifras y palabras, para pintar las cifras en mono (.text-number) sin armar HTML:
 * «1,240 de 4,512» → [{t:'1,240', cifra:true}, {t:' de ', cifra:false}, {t:'4,512', cifra:true}].
 */
export function partesCifra(texto) {
  var s = String(texto === null || texto === undefined ? '' : texto);
  var r = [];
  var re = /\d[\d,]*(?:\.\d+)?/g;
  var desde = 0, m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > desde) r.push({ t: s.slice(desde, m.index), cifra: false });
    r.push({ t: m[0], cifra: true });
    desde = m.index + m[0].length;
  }
  if (desde < s.length) r.push({ t: s.slice(desde), cifra: false });
  return r;
}

/**
 * ¿Algo quedó pendiente al terminar? (R24: el paso 5 no dice «todo ya vive en Factura Digital» si no es cierto.)
 * cfdis: algún mes de emitidos o recibidos se cerró sin traer; catalogos: algún catálogo quedó pendiente.
 */
export function pendientes(renglones) {
  var r = { cfdis: false, catalogos: false };
  var lista = Array.isArray(renglones) ? renglones : [];
  for (var i = 0; i < lista.length; i++) {
    var x = lista[i];
    if (!x || x.estado !== 'fallido') continue;
    if (CFDIS[x.clave]) r.cfdis = true; else r.catalogos = true;
  }
  return r;
}

var SOPORTE = 'escríbenos a soporte@facturadigital.com.mx y la terminamos contigo.';

/**
 * Lo que dice y ofrece el paso 5 (R24, ola final A1 y A7), del estado del servidor: {casi, titulo, sub, reintentar,
 * notaNoDisponibles, regaloDisponible}. Con meses sin traer, «casi lista» y «Volver a intentar» (reintentar_ajax); con un
 * catálogo pendiente, soporte. Lo no disponible en el SAT (resumen.no_disponibles) va en una nota y no la vuelve «casi
 * lista». regaloDisponible: el regalo sigue sin usar y no hay meses que reintentar (Migracion_asistente::estado()).
 */
export function paso5(estado) {
  var e = estado || {};
  var p = pendientes(e.renglones);
  var casi = p.cfdis || p.catalogos;
  var sub = 'Todo lo de tu sistema anterior ya vive en Factura Digital, ordenado y listo para usarse.';
  if (p.cfdis) {
    sub = 'Algunos meses quedaron pendientes; puedes volver a intentarlo desde aquí.'
      + (p.catalogos ? ' Para la parte de tus catálogos que quedó pendiente, ' + SOPORTE : '');
  } else if (p.catalogos) {
    sub = 'Una parte de tus catálogos quedó pendiente: ' + SOPORTE;
  }
  var n = entero(e.resumen && e.resumen.no_disponibles);
  var nota = '';
  if (n !== null && n > 0) {
    nota = (n === 1 ? '1 CFDI no está disponible' : numero(n) + ' CFDIs no están disponibles') + ' para descarga en el SAT'
      + (casi ? '.' : '; todo lo demás ya está en tu cuenta.');
  }
  return {
    casi: casi,
    titulo: casi ? 'Tu cuenta está casi lista' : 'Tu cuenta está lista',
    sub: sub,
    reintentar: p.cfdis,
    notaNoDisponibles: nota,
    regaloDisponible: !!e.regalo_disponible && !p.cfdis,
  };
}

/**
 * ¿La región en vivo debe decir el avance? Solo al cruzar una decena y al llegar a 100 (y la primera vez): el sondeo
 * de 4 s no puede hablarle al lector de pantalla en cada vuelta.
 */
export function debeAnunciar(nuevo, anunciado) {
  var n = entero(nuevo);
  if (n === null) return false;
  var a = entero(anunciado);
  if (a === null) return true;
  if (n >= 100 && a < 100) return true;
  return Math.floor(n / 10) > Math.floor(a / 10);
}

function normalizar(s) {
  return String(s === null || s === undefined ? '' : s).toLowerCase()
    .replace(/[áàä]/g, 'a').replace(/[éèë]/g, 'e').replace(/[íìï]/g, 'i').replace(/[óòö]/g, 'o').replace(/[úùü]/g, 'u')
    .trim();
}

/** ¿La sugerencia coincide con lo que se busca? En descripción, claves y nombres del catálogo, sin acentos. */
export function coincide(producto, consulta) {
  var q = normalizar(consulta);
  if (q === '') return true;
  var p = producto || {};
  var donde = [p.descripcion, p.claveprodserv, p.claveprodserv_nombre, p.claveunidad, p.claveunidad_nombre].map(normalizar).join(' ');
  return donde.indexOf(q) !== -1;
}

var PASOS = [
  { t: 'Conecta con el SAT', d: 'e.firma o Contraseña del SAT' },
  { t: 'Elige qué traer', d: 'CFDIs y catálogos' },
  { t: 'Migración', d: 'Nosotros nos encargamos' },
  { t: 'Tus productos', d: 'Elige cuáles importar' },
  { t: 'Listo', d: 'A facturar' },
];

function tieneRenglon(renglones, clave) {
  for (var i = 0; i < renglones.length; i++) if (renglones[i] && renglones[i].clave === clave) return renglones[i];
  return null;
}

/**
 * El riel de los cinco pasos: [{n, t, d, estado}] con estado 'hecho' | 'actual' | 'pendiente'. `paso` es el que se ve
 * y `visibles`, cuántas opciones ofreció el paso 2 (las que el permiso deja ver), para «5 de 5 elegidos». Las
 * descripciones de lo hecho son las del mockup («e.firma lista», «Recibidos en camino», «Completa», «96 importados»).
 */
export function riel(estado, paso, visibles) {
  var e = estado || {};
  var renglones = Array.isArray(e.renglones) ? e.renglones : [];
  var tipo = e.credencial && e.credencial.tipo;
  var r = [];
  for (var i = 0; i < PASOS.length; i++) {
    var n = i + 1;
    r.push({ n: n, t: PASOS[i].t, d: PASOS[i].d, estado: n < paso ? 'hecho' : (n === paso ? 'actual' : 'pendiente') });
  }

  if (r[0].estado === 'hecho' && tipo) r[0].d = tipo === 'fiel' ? 'e.firma lista' : 'Contraseña del SAT lista';
  if (r[1].estado === 'hecho' && renglones.length) r[1].d = numero(renglones.length) + ' de ' + numero(Math.max(visibles || 0, renglones.length)) + ' elegidos';
  if (r[2].estado === 'hecho') {
    if (e.fase === 'migrando') {
      var rec = tieneRenglon(renglones, 'recibidos'), emi = tieneRenglon(renglones, 'emitidos');
      var abierto = function (x) { return x && (x.estado === 'run' || x.estado === 'wait'); };
      r[2].d = abierto(emi) ? 'Emitidos en camino' : (abierto(rec) ? 'Recibidos en camino' : 'En camino');
    } else {
      r[2].d = 'Completa';
    }
  }
  if (paso === 5) {
    var prod = tieneRenglon(renglones, 'productos');
    r[3].estado = 'hecho';
    if (!prod) {
      r[3].d = 'Sin productos esta vez';
    } else if (e.productos_estado === 'importada') {
      var k = e.resumen && entero(e.resumen.productos) !== null ? entero(e.resumen.productos) : 0;
      r[3].d = numero(k) + (k === 1 ? ' importado' : ' importados');
    } else if (e.productos_estado === 'descartada') {
      r[3].d = 'Lista cerrada';
    } else if (e.productos_estado === 'lista') {
      r[3].d = 'Tu lista te espera';
    }
  }
  return r;
}
